import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getHomelessInfo } from 'redux/actions/homeless';
import { updateHomelessPrivateInfo } from 'api/homeless';
import { Homeless } from 'types';

interface AdminMemberInfoFormData {
    profile_stage: string;
    is_development: boolean;
    membership_extension: boolean;
    membership_end_date: Date | null;
    deployment_id: number | null;
    organization_id: number | null;
}

const useUpdateAdminMemberInfo = (homeless: Homeless) => {
    const dispatch = useDispatch();

    const [error, setError] = useState<null | string>(null);
    const [isLoading, setIsLoading] = useState(false);

    const [formData, setFormData] = useState<AdminMemberInfoFormData>({
        profile_stage: homeless.profile_stage || 'LIVE',
        is_development: homeless.is_development || false,
        membership_extension: homeless.membership_extension || false,
        membership_end_date: homeless.membership_end_date ? new Date(homeless.membership_end_date) : null,
        deployment_id: homeless.deployment_id || null,
        organization_id: homeless.organization_id || null
    });

    const updateField = useCallback(<K extends keyof AdminMemberInfoFormData>(
        field: K,
        value: AdminMemberInfoFormData[K]
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const submitChanges = useCallback(async () => {
        try {
            setError(null);
            setIsLoading(true);

            const data = new FormData();
            if (formData.profile_stage !== homeless.profile_stage) {
                data.append('samaritan_member[profile_stage]', formData.profile_stage);
            }
            if (formData.is_development !== homeless.is_development) {
                data.append('samaritan_member[is_development_profile]', String(formData.is_development));
            }
            if (formData.membership_extension !== homeless.membership_extension) {
                data.append('samaritan_member[membership_extension]', String(formData.membership_extension));
            }
            if (formData.membership_end_date !== (homeless.membership_end_date ? new Date(homeless.membership_end_date) : null)) {
                data.append('samaritan_member[membership_end_date]', formData.membership_end_date?.toISOString() || '');
            }
            if (formData.deployment_id !== homeless.deployment_id) {
                data.append('samaritan_member[deployment_id]', String(formData.deployment_id));
            }
            if (formData.organization_id !== homeless.organization_id) {
                data.append('samaritan_member[organization_id]', String(formData.organization_id));
            }

            const res = await updateHomelessPrivateInfo(homeless.id, data);

            if (!res.ok) {
                const errorData = await res.json();
                if (errorData?.errors?.length) {
                    const { resource, attribute, description } = errorData.errors[0];
                    const errorMsg = `${resource} ${attribute}: ${description}`;
                    throw new Error(errorMsg);
                }
            }
            
            // Update the redux store 
            setTimeout(() => {
                dispatch(getHomelessInfo('', '', '', homeless.id));
            }, 1500);
            
            return res;
        } catch (err) {
            console.error('Update member info failed:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, formData, homeless.id, homeless.deployment_id, homeless.is_development, homeless.membership_extension, homeless.membership_end_date, homeless.organization_id, homeless.profile_stage]);

    const reset = useCallback(() => {
        setFormData({
            profile_stage: homeless.profile_stage || 'LIVE',
            is_development: homeless.is_development || false,
            membership_extension: homeless.membership_extension || false,
            membership_end_date: homeless.membership_end_date ? new Date(homeless.membership_end_date) : null,
            deployment_id: homeless.deployment_id || null,
            organization_id: homeless.organization_id || null
        });
        setIsLoading(false);
        setError(null);
    }, [homeless]);

    return {
        formData,
        updateField,
        submitChanges,
        reset,
        error,
        isLoading
    };
};

export default useUpdateAdminMemberInfo; 